// React-query hooks for the monitoring section. Query keys are namespaced
// under ["monitoring", …] so a tariff save can invalidate every period view
// at once (live mode computes ₹ values server-side from the stored tariff;
// mock mode passes the tariff straight into the mock readers, hence the
// tariff in the key only matters there — keeping it in the key is harmless
// for live mode because a save invalidates everything anyway).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchDay,
  fetchLifetime,
  fetchMonth,
  fetchPlants,
  fetchSummary,
  fetchYear,
  savePlantSettings,
  saveTariff,
  MONITORING_MOCK,
} from "./data";

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function useMonitoringSummary(plantId) {
  return useQuery({
    queryKey: ["monitoring", "summary", plantId ?? null],
    queryFn: () => fetchSummary(plantId),
  });
}

export function useDayData(anchor, tariff, plantId) {
  return useQuery({
    queryKey: ["monitoring", "day", dayKey(anchor), tariff, plantId ?? null],
    queryFn: () => fetchDay(anchor, tariff, plantId),
  });
}

export function useMonthData(year, month, tariff, plantId) {
  return useQuery({
    queryKey: ["monitoring", "month", year, month, tariff, plantId ?? null],
    queryFn: () => fetchMonth(year, month, tariff, plantId),
  });
}

export function useYearData(year, tariff, plantId) {
  return useQuery({
    queryKey: ["monitoring", "year", year, tariff, plantId ?? null],
    queryFn: () => fetchYear(year, tariff, plantId),
  });
}

export function useLifetime(plantId) {
  return useQuery({
    queryKey: ["monitoring", "lifetime", plantId ?? null],
    queryFn: () => fetchLifetime(plantId),
  });
}

// Plant list for the admin selector (admin-only endpoint; enabled gates it).
export function usePlants(enabled) {
  return useQuery({ queryKey: ["monitoring", "plants"], queryFn: fetchPlants, enabled });
}

export function useSaveTariff(plantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tariff) => saveTariff(tariff, plantId),
    onSuccess: () => {
      if (!MONITORING_MOCK) queryClient.invalidateQueries({ queryKey: ["monitoring"] });
    },
  });
}

// Settings → "Your system" edits (capacity, install date, location, inverter).
// A successful save invalidates every monitoring query so the summary — and
// all ₹/kWh figures derived from systemKwp — refresh from the server.
export function useSavePlantSettings(plantId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => savePlantSettings(patch, plantId),
    onSuccess: () => {
      if (!MONITORING_MOCK) queryClient.invalidateQueries({ queryKey: ["monitoring"] });
    },
  });
}
